import { type FunctionComponent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Checkbox, Empty, Radio, Space, Spin, Switch, Table, Typography, message } from "antd";
import { useTranslation } from "react-i18next";

import {
  CHALLENGE_MANAGER_NOTIFICATIONS,
  type ChallengeManagerEntity,
  type ChallengeManagerNotificationKey,
} from "@fitness-app/data-models/entities/ChallengeFeed";
import { type Database } from "@fitness-app/supabase";
import { getParentTrainerId } from "@fitness-app/app-store/src/store/reducers/user/selectors";

import { supabase, useAppSelector } from "~/store/initializeStore";

type TeamMemberRow = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  role: string | null;
};

const managersQueryKey = (challengeId: string) => ["challenge-managers", challengeId];
const teamQueryKey = (trainerId: string | null) => ["challenge-managers-team", trainerId];

/**
 * TRE-1655 — the owner picks who manages THIS challenge.
 *
 * An empty list means owner-only: `challenge_can_manage` gates every challenge policy (participants,
 * tasks, completions, board), so unchecking someone here takes the whole challenge away from them,
 * not just the board. One manager can be marked as the board's caretaker — auto-created tasks for
 * unanswered participant posts are assigned to them.
 */
const ChallengeManagersPanel: FunctionComponent<{ challengeId: string }> = ({ challengeId }) => {
  const { t } = useTranslation("team");
  const parentId = useAppSelector(getParentTrainerId);
  const userId = useAppSelector((state) => state.user.data?.id ?? null);
  const queryClient = useQueryClient();
  // Only the organisation owner may write the assignments (RLS enforces it too) — a collaborator
  // seeing editable switches they cannot save would be a trap.
  const isOwner = Boolean(userId && parentId && userId === parentId);

  const teamQuery = useQuery({
    queryKey: teamQueryKey(parentId),
    enabled: Boolean(parentId),
    queryFn: async (): Promise<TeamMemberRow[]> => {
      // Menedżer wyzwania dostaje powiadomienia z tablicy i wjazd w zadania/zaliczenia —
      // oferujemy tylko członków, których rola (albo indywidualne nadpisanie) ma
      // engagement.manage. Czytamy żywy katalog, więc edytor ról działa tu od razu.
      const [membersResult, baselineResult, grantResult] = await Promise.all([
        supabase
          .from("team_member")
          .select("id, firstName, lastName, email, role")
          .eq("trainer", parentId ?? "")
          .eq("status", "ACTIVE")
          .neq("disabled", true),
        supabase.from("role_permission").select("role").eq("key", "engagement.manage").neq("scope", "NONE"),
        supabase.from("team_member_permission").select("memberId").eq("key", "engagement.manage").neq("scope", "NONE"),
      ]);
      const error = membersResult.error ?? baselineResult.error ?? grantResult.error;
      if (error) {
        throw new Error(error.message);
      }
      const engagementRoles = new Set((baselineResult.data ?? []).map((row) => row.role as string));
      const engagementMembers = new Set((grantResult.data ?? []).map((row) => row.memberId as string));
      return ((membersResult.data ?? []) as TeamMemberRow[]).filter(
        (member) => (member.role && engagementRoles.has(member.role)) || engagementMembers.has(member.id),
      );
    },
  });

  const managersQuery = useQuery({
    queryKey: managersQueryKey(challengeId),
    enabled: Boolean(challengeId),
    queryFn: async (): Promise<ChallengeManagerEntity[]> => {
      const { data, error } = await supabase.from("challenge_manager").select("*").eq("challengeId", challengeId);
      if (error) {
        throw new Error(error.message);
      }
      return (data ?? []) as ChallengeManagerEntity[];
    },
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: managersQueryKey(challengeId) });
  };

  const assign = useMutation({
    mutationFn: async ({ teamMemberId, assigned }: { teamMemberId: string; assigned: boolean }) => {
      if (assigned) {
        const { error } = await supabase.from("challenge_manager").insert({
          challengeId,
          teamMemberId,
          createdBy: userId,
        } satisfies Database["public"]["Tables"]["challenge_manager"]["Insert"]);
        if (error) {
          throw new Error(error.message);
        }
        return;
      }
      const { error } = await supabase
        .from("challenge_manager")
        .delete()
        .eq("challengeId", challengeId)
        .eq("teamMemberId", teamMemberId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: invalidate,
    onError: (error: Error) => void message.error(`Nie udało się zapisać: ${error.message}`),
  });

  const setBoardOwner = useMutation({
    mutationFn: async (teamMemberId: string) => {
      // The partial unique index allows one caretaker per challenge, so clear the old one first.
      const { error: clearError } = await supabase
        .from("challenge_manager")
        .update({ isBoardOwner: false } satisfies Database["public"]["Tables"]["challenge_manager"]["Update"])
        .eq("challengeId", challengeId)
        .eq("isBoardOwner", true);
      if (clearError) {
        throw new Error(clearError.message);
      }
      const { error } = await supabase
        .from("challenge_manager")
        .update({ isBoardOwner: true } satisfies Database["public"]["Tables"]["challenge_manager"]["Update"])
        .eq("challengeId", challengeId)
        .eq("teamMemberId", teamMemberId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: invalidate,
    onError: (error: Error) => void message.error(`Nie udało się zapisać: ${error.message}`),
  });

  const setNotification = useMutation({
    mutationFn: async ({
      teamMemberId,
      key,
      value,
    }: {
      teamMemberId: string;
      key: ChallengeManagerNotificationKey;
      value: boolean;
    }) => {
      const { error } = await supabase
        .from("challenge_manager")
        // A computed key widens to `{ [x: string]: boolean }`, which `satisfies` cannot narrow —
        // the cast is safe because `key` is constrained to the switch names by its own union type.
        .update({ [key]: value } as Database["public"]["Tables"]["challenge_manager"]["Update"])
        .eq("challengeId", challengeId)
        .eq("teamMemberId", teamMemberId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: invalidate,
    onError: (error: Error) => void message.error(`Nie udało się zapisać: ${error.message}`),
  });

  // `isInitialLoading`, not `isLoading`: a disabled query (no `parentId` yet) counts as loading in
  // react-query v4 and would pin this panel on a spinner.
  if (teamQuery.isInitialLoading || managersQuery.isInitialLoading) {
    return (
      <div className="py-8 text-center">
        <Spin />
      </div>
    );
  }

  const managers = managersQuery.data ?? [];
  const byTeamMember = new Map(managers.map((manager) => [manager.teamMemberId, manager]));
  const team = teamQuery.data ?? [];

  if (!team.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak członków zespołu do przypisania" />;
  }

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {isOwner ? null : (
        <Alert
          type="info"
          showIcon
          message="Tylko właściciel konta może zmieniać przypisania"
          description="Widzisz, kto zarządza tym wyzwaniem, ale zmian dokonuje właściciel organizacji."
        />
      )}
      {managers.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message="Nikt nie jest przypisany — wyzwaniem zarządza wyłącznie właściciel"
          description="Dotyczy całego wyzwania: zadań, uczestniczek, zaliczeń i tablicy."
        />
      ) : null}

      <Table<TeamMemberRow>
        rowKey="id"
        dataSource={team}
        pagination={false}
        size="middle"
        columns={[
          {
            title: "Członek zespołu",
            key: "member",
            render: (_, member) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong>
                  {[member.firstName, member.lastName].filter(Boolean).join(" ") || member.email}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {/* `roleShort`, not `role`: the full labels are access descriptions ("Trener -
                      dostęp tylko do przypisanych klientów") and would not fit this 12px caption. */}
                  {member.role ? t(`roleShort.${member.role.toLowerCase()}`, { defaultValue: member.role }) : "—"}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: "Zarządza",
            key: "assigned",
            width: 110,
            render: (_, member) => (
              <Checkbox
                checked={byTeamMember.has(member.id)}
                disabled={!isOwner || assign.isLoading}
                onChange={(event) => assign.mutate({ teamMemberId: member.id, assigned: event.target.checked })}
              />
            ),
          },
          {
            title: "Opiekun tablicy",
            key: "boardOwner",
            width: 140,
            render: (_, member) => {
              const manager = byTeamMember.get(member.id);
              return (
                <Radio
                  checked={Boolean(manager?.isBoardOwner)}
                  disabled={!isOwner || !manager || setBoardOwner.isLoading}
                  onChange={() => setBoardOwner.mutate(member.id)}
                />
              );
            },
          },
          ...CHALLENGE_MANAGER_NOTIFICATIONS.map((notification) => ({
            title: notification.label,
            key: notification.key,
            width: 160,
            render: (_: unknown, member: TeamMemberRow) => {
              const manager = byTeamMember.get(member.id);
              return (
                <Switch
                  size="small"
                  checked={Boolean(manager?.[notification.key])}
                  disabled={!isOwner || !manager || setNotification.isLoading}
                  onChange={(value) =>
                    setNotification.mutate({ teamMemberId: member.id, key: notification.key, value })
                  }
                />
              );
            },
          })),
        ]}
      />
    </Space>
  );
};

export default ChallengeManagersPanel;
